import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Steps } from 'antd';
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { MdPending, MdOutlineError } from "react-icons/md";
import { formatDateHandler, formatDateHandler2, toTitleCase } from "utils/common";
import { Button } from "components/Button";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FilterSelect from "components/Form/filterSelect";
import { customStyleSelect } from "utils/common";
import { leaveStatusOptions } from "utils/constants";
import { Bounce, toast } from "react-toastify";
import { fetchLeaveByToken, approveLeaveByToken } from "api/services/leave"; // You'll need to create these API methods
import Image from "components/Image";
import DateInput from "components/Form/dateInput";
import LeaveAttachment from "views/pages/Employees/leaves/leaveAttachment";
import { useNavigate } from "react-router-dom";
const LeaveApproval = () => {
  const [loading, setLoading] = useState(false);
  const [leaveDetails, setLeaveDetails] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [currentStatusStep, setCurrentStatusStep] = useState(0);
  const [statusSteps, setStatusSteps] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // Parse query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const leaveApprovalSchema = Yup.object().shape({
    approval_status: Yup.object().required('Status is required'),
    comment: Yup.string(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError: setFormError,
    reset
  } = useForm({
    resolver: yupResolver(leaveApprovalSchema),
    defaultValues: {}
  });

  useEffect(() => {
    if (!token) {
      setError("No approval token found in URL");
      return;
    }

    fetchLeaveDetails();
  }, [token]);

  const fetchLeaveDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchLeaveByToken(token);
      setLeaveDetails(response.data);
      
      // Filter status options to exclude "cancelled"
      setStatusOptions(leaveStatusOptions.filter(item => item.value !== "cancelled"));
      
      createApprovalSteps(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leave details:", error);
      setError("Failed to fetch leave details. The token might be invalid or expired.");
      setLoading(false);
    }
  };

  const createApprovalSteps = (leave) => {
    let generated_steps = [];
    let current_step = leave?.approval_level;
    let existing_steps = leave?.leave_approval || [];
    
    if (leave?.approval_status === "cancelled") {
      generated_steps.push({
        title: 'Request',
        subTitle: "Cancelled",
        description: <span className="text-wrap lh-1" style={{fontSize:9}}>
          {`Requested on ${formatDateHandler2(leave?.created_at)} by ${leave?.request_created_by?.first_name} ${leave?.request_created_by?.last_name} ${leave?.comment ? " | Comment : " + leave?.comment : ""}`}
        </span>,
        icon: <FaTimesCircle color="red" size={35} />
      });
    } else {
      generated_steps.push({
        title: 'Request',
        subTitle: "Created",
        description: <span className="text-wrap lh-1" style={{fontSize:9}}>
          {`Requested on ${formatDateHandler2(leave?.created_at)} by ${leave?.request_created_by?.first_name} ${leave?.request_created_by?.last_name} ${leave?.comment ? " | Comment : " + leave?.comment : ""}`}
        </span>,
        icon: <FaCheckCircle color="green" size={35} />
      });
    }
    
    existing_steps.forEach(item => {
      let stepDetails = {
        title: item?.approval_type === "request" ? 'Approval' : 'Cancellation',
        subTitle: toTitleCase(item?.status)
      };
      
      // Set icon and description based on status in one switch
      switch(item?.status) {
        case "pending":
          stepDetails.icon = <MdPending size={35} />;
          stepDetails.description = <span className="text-wrap lh-1" style={{fontSize:9}}>
            {`Pending approval ${item?.comment ? "| Comment: " + item?.comment : ''}`}
          </span>;
          break;
          
        case "rejected":
          stepDetails.icon = <FaTimesCircle color="red" size={35} />;
          stepDetails.description = <span className="text-wrap lh-1" style={{fontSize:9}}>
            {`Rejected on ${formatDateHandler2(item?.approved_on)} ${item?.comment ? "| Comment: " + item?.comment : ''}`}
          </span>;
          break;
          
          case "escalated":
            stepDetails.title = "Escalated"
            stepDetails.subTitle = ""
            stepDetails.icon = <i className="fa fa-exclamation-triangle" style={{ color: "#FFC107", fontSize: "35px" }} />;
            stepDetails.description = <span className="text-wrap lh-1 text-dark" style={{fontSize:9}}>
              {`Escalated on ${formatDateHandler2(item?.approved_on)} by ${item?.approver?.first_name || ""} ${item?.approver?.last_name || ""} ${item?.comment ? "| Comment: " + item?.comment : ''}`}
            </span>;
            break;
            
        default: // approved or any other status
            stepDetails.icon = <FaCheckCircle color="green" size={35} />;
            stepDetails.description = <span className="text-wrap lh-1 text-dark" style={{fontSize:9}}>
                {`${item?.status=="pending"? item.approver?.first_name+" "+ item.approver?.last_name : toTitleCase(item.status)+" on "+ formatDateHandler2(item?.approved_on) +" by "+item.approver?.first_name+" "+ item.approver?.last_name}  ${item?.comment ? "| Comment : " + item?.comment : ''}`}
                {/* {`${toTitleCase(item.status)} on ${formatDateHandler2(item?.approved_on)} ${item?.comment ? "| Comment: " + item?.comment : ''}`} */}
            </span>;
      }
      
      generated_steps.push(stepDetails);
    });
    
    setCurrentStatusStep(current_step);
    setStatusSteps(generated_steps);
  };

  const onStatusChange = async (data) => {
    setLoading(true);
    
    try {
      const payload = {
        approval_status: data?.approval_status?.value,
        comment: data?.comment,
        token: token
      };
      
      const response = await approveLeaveByToken(payload);
      if(response.status === 200){
        toast.success('Leave status updated successfully!', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        navigate("/login");
      }else{
        toast.error(response.data.error, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      }
      
      // Refresh the leave details
      fetchLeaveDetails();
      reset();
      
    } catch (e) {
      console.error(e);
      setLoading(false);
      
      try {
        if (e.response?.data) {
          Object.entries(e.response.data).forEach(([k, v]) => {
            setFormError(k, {
              type: 'manual',
              message: Array.isArray(v) ? v.join(", ") : v
            });
          });
        } else {
          toast.error(e.response?.data?.error || "Error updating leave status!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        }
      } catch (err) {
        toast.error("An unexpected error occurred", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    }
  };

  const LeaveAddressSelected = ({address}) => {
    return (
      <div className="border-bottom pb-2 mb-2 card mt-3 p-2">
        <strong className="">Leave Address</strong>
        <p className="mb-1">
          <strong>Name:</strong> {address?.name}
        </p>
        <p className="mb-1">
          <strong>Address:</strong> {address?.address_1}, {address?.address_2}
        </p>
        <p className="mb-1">
          <strong>City:</strong> {address?.city}
        </p>
        <p className="mb-1">
          <strong>Country:</strong> {address?.country?.name}
        </p>
        <p className="mb-1">
          <strong>Emergency Contact Name:</strong> {address?.emergency_contact_name}
        </p>
        <p className="mb-1">
          <strong>Emergency Contact Number:</strong> {address?.emergency_contact_number}
        </p>
      </div>
    );
  };

  if (error) {
    return (
      <div className="account-page">
        <div className="main-wrapper">
          <div className="account-content">
            <div className="container">
              <div className="account-box " style={{width:"100%"}}>
                <div className="account-wrapper">
                  <div className="account-logo">
                    <img src="https://storage.mealtrackr.io/internal/Nandos-Logo.png" alt="Company" />
                  </div>
                  <div className="card p-4">
                    <div className="text-center">
                      <MdOutlineError size={60} color="red" />
                      <h3 className="mt-3">Error</h3>
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !leaveDetails) {
    return (
      <div className="account-page">
        <div className="main-wrapper">
          <div className="account-content">
            <div className="container">
              <div className="account-box" style={{width:"100%"}}>
                <div className="account-wrapper text-center">
                  <div className="account-logo">
                    <img src="https://storage.mealtrackr.io/internal/Nandos-Logo.png" alt="Company Logo" />
                  </div>
                  <div className="card p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3">Loading leave details...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="main-wrapper">
        <div className="account-content">
          <div className="container">
            <div className="account-box" style={{width:"100%"}}>
              <div className="account-wrapper">
                <div className="account-logo">
                  <img src="https://storage.mealtrackr.io/internal/Nandos-Logo.png" alt="Company Logo" />
                </div>
                <h3 className="account-title">Leave Approval</h3>
                <p className="account-subtitle">Approve or reject leave request</p>
                
                {leaveDetails?.approval_status === "approved" || leaveDetails?.approval_status === "rejected" ? (
                  <div className="alert alert-info">
                    This leave request has already been {leaveDetails.approval_status}.
                    No further action is required.
                  </div>
                ) : null}
                
                <div className="card p-3 mb-4">
                  <label className="col-form-label">Approval Status</label>
                  <Steps
                    current={currentStatusStep}
                    items={statusSteps}
                  />
                </div>
                
                <div className="row">
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">User</label>
                    <input
                      className="form-control"
                      readOnly
                      value={leaveDetails ? `${leaveDetails?.company_user?.first_name} ${leaveDetails?.company_user?.last_name}` : ""}
                      type="text"
                    />
                  </div>

                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">Leave Type</label>
                    <input
                      className="form-control"
                      readOnly
                      value={leaveDetails?.leave_type?.description || ""}
                      type="text"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">From</label>
                    <DateInput
                      className="form-control floating datetimepicker"
                      disabled
                      selected={leaveDetails?.start_date ? new Date(leaveDetails.start_date) : null}
                      dateFormat="dd-MM-yyyy"
                    />
                  </div>
                  
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">To</label>
                    <DateInput
                      className="form-control floating datetimepicker"
                      disabled
                      selected={leaveDetails?.end_date ? new Date(leaveDetails.end_date) : null}
                      dateFormat="dd-MM-yyyy"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">Number of Days</label>
                    <input 
                      className="form-control" 
                      readOnly 
                      type="text" 
                      value={leaveDetails?.no_of_days || ""} 
                    />
                  </div>
                  
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">Current Status</label>
                    <input 
                      className="form-control" 
                      readOnly 
                      type="text" 
                      value={(leaveDetails?.approval_status || "").toUpperCase()} 
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">Comment</label>
                    <textarea
                      rows={4}
                      readOnly
                      className="form-control"
                      value={leaveDetails?.comment || ""}
                    />

                    {leaveDetails?.leave_address && 
                      <LeaveAddressSelected address={leaveDetails.leave_address} />
                    }

                    {leaveDetails?.role_delegation && 
                      <div className="border-bottom pb-2 mb-2 card mt-3 p-2">
                        <strong className="mb-1">Role Delegation</strong>
                        <p className="table-avatar d-flex">
                          <a className="avatar">
                            <Image 
                              src={leaveDetails.role_delegation?.profile_image} 
                              style={{ objectFit: "cover" }} 
                              type="user"
                            />
                          </a>
                          <div>
                            <a className="profile-split d-flex flex-column">
                              {leaveDetails.role_delegation?.first_name} {leaveDetails.role_delegation?.last_name}
                            </a>
                            {leaveDetails.role_delegation?.emp_no && (
                              <span className="small text-muted">{leaveDetails.role_delegation?.emp_no}</span>
                            )}
                          </div>
                        </p>
                      </div>
                    }

                
                  </div>

                  <div className="input-block mb-3 col-md-6 col-sm-12">
                    <label className="col-form-label">Attachments</label>
                    {leaveDetails?.attachments && (
                      <LeaveAttachment fields={leaveDetails.attachments} />
                    )}
                  </div>
                  {leaveDetails && leaveDetails.approval_status === "pending" && (
                      <form className="d-flex flex-column align-items-center" onSubmit={handleSubmit(onStatusChange)}>
                        <label className="col-form-label mt-3">
                          Your Comment
                        </label>
                        <Controller
                          name="comment"
                          control={control}
                          render={({ field }) => (
                            <textarea
                              rows={4}
                              cols={5}
                              className="form-control w-50"
                              value={field?.value || ""}
                              onChange={field?.onChange}
                              placeholder="Add your comment here (optional)"
                            />
                          )}
                        />
                        {errors.comment && (
                          <small className="text-danger">
                            {errors.comment?.message}
                          </small>
                        )}

                        <div className="submit-section justify-content-center mt-3 d-flex gap-2">
                          <Button
                            loading={loading}
                            className="btn btn-success submit-btn"
                            type="button"
                            label={leaveDetails?.approval_status === "pending" ? "Accept Approval" : "Accept Cancellation"}
                            onClick={() => {
                                if(leaveDetails?.approval_status === "pending"){
                                  setValue("approval_status", { value: "approved", label: "Approved" });
                                  handleSubmit(onStatusChange)();
                                }else{
                                  setValue("approval_status", { value: "cancel_approved", label: "Cancel Approved" });
                                  handleSubmit(onStatusChange)();
                                }
                            }}
                          />
                          <Button
                            loading={loading}
                            className="btn btn-danger submit-btn"
                            type="button"
                            label={leaveDetails?.approval_status === "pending" ? "Reject Approval" : "Reject Cancellation"}
                            onClick={() => {
                              if(leaveDetails?.approval_status === "pending"){
                                setValue("approval_status", { value: "rejected", label: "Rejected" });
                                handleSubmit(onStatusChange)();
                              }else{
                                setValue("approval_status", { value: "cancel_rejected", label: "Cancel Rejected" });
                                handleSubmit(onStatusChange)();
                              }
                            }}
                          />
                        </div>
                      </form>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveApproval;
